import React, { useState, useEffect } from "react";
import {
  Phone,
  Video,
  MoreVertical,
  Smile,
  Paperclip,
  Mic,
  Eye,
  Code
} from "lucide-react";

const WhatsAppNewPreview = ({ templateData, defaultValues = {} }) => {
  const [selectedWaba, setSelectedWaba] = useState(null);
  const [showVariables, setShowVariables] = useState(false); // Toggle between variables and sample values

  // Force re-render when defaultValues change
  useEffect(() => {
    // This ensures the component updates when defaultValues prop changes
  }, [defaultValues]);

  // Get selected WABA account from localStorage
  useEffect(() => {
    console.log("Template Data:", templateData);
    const savedWaba = localStorage.getItem("selectedWabaAccount");
    if (savedWaba) {
      try {
        const parsed = JSON.parse(savedWaba);
        setSelectedWaba(parsed);
      } catch (err) {
        console.error("Error parsing saved WABA account:", err);
      }
    }

    // Listen for WABA account changes
    const handleWabaChange = (event) => {
      setSelectedWaba(event.detail);
    };

    const handleStorageChange = () => {
      const savedWaba = localStorage.getItem("selectedWabaAccount");
      if (savedWaba) {
        try {
          const parsed = JSON.parse(savedWaba);
          setSelectedWaba(parsed);
        } catch (err) {
          console.error("Error parsing saved WABA account:", err);
        }
      }
    };

    window.addEventListener("wabaAccountChanged", handleWabaChange);
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleStorageChange);

    return () => {
      window.removeEventListener("wabaAccountChanged", handleWabaChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, []);

  /**
   * Get value for a variable - either from defaultValues, sample, or placeholder
   * @param {string} variableName - The variable number (e.g., "1", "2")
   * @param {string} componentType - The component type ("HEADER" or "BODY")
   */
  const getVariableValue = (variableName, componentType = "BODY") => {
    if (showVariables) {
      return `{{${variableName}}}`;
    }
    
    // Check if there's a default value for this variable
    // Variables are stored as header_1, body_1, etc. in defaultValues
    const key = componentType === "HEADER" 
      ? `header_${variableName}` 
      : `body_${variableName}`;
    
    if (defaultValues[key] && defaultValues[key].trim() !== '') {
      return defaultValues[key];
    }
    
    // Fall back to sample value from template
    const variable = templateData.variables?.find(v => v.name === variableName);
    return variable?.sample || `{{${variableName}}}`;
  };

  const renderHeader = () => {
    // Check for headerFormat (uppercase) or headerType (lowercase) for backward compatibility
    const headerFormat = templateData.headerFormat || templateData.headerType;
    const headerMediaUrl = templateData.headerFilePreview || templateData.header_media_url;
    
    if (!headerFormat || headerFormat === "NONE" || headerFormat === "none") {
      return null;
    }

    // Normalize to uppercase for comparison
    const format = headerFormat.toUpperCase();

    // Handle TEXT header
    if (format === "TEXT" && templateData.headerText) {
      let headerText = templateData.headerText;

      // Replace variables with values based on toggle state
      // Extract header variables from headerText
      const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
      headerVarMatches.forEach((match) => {
        const varNum = match.replace(/\{\{|\}\}/g, '');
        const regex = new RegExp(`\\{\\{${varNum}\\}\\}`, "g");
        headerText = headerText.replace(regex, getVariableValue(varNum, "HEADER"));
      });

      return (
        <div className="mb-2 -mx-1 -mt-1 px-2 py-1.5 bg-gray-50 rounded-t-lg border-b border-gray-200">
          <p className="text-[13px] font-semibold text-[#111B21]">
            {headerText}
          </p>
        </div>
      );
    }

    if (format === "TEXT" && !templateData.headerText) {
      return null;
    }

    if (format === "IMAGE" && headerMediaUrl) {
      return (
        <div className="mb-2 -mx-1 -mt-1 rounded-t-lg overflow-hidden bg-gray-50 flex items-center justify-center">
          <img
            src={headerMediaUrl}
            alt="Preview"
            className="max-w-full max-h-48 w-auto h-auto object-contain"
          />
        </div>
      );
    }

    if (format === "IMAGE" && !headerMediaUrl) {
      return (
        <div className="mb-2 -mx-1 -mt-1 bg-gray-200 h-32 rounded-t-lg flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-1">
              <svg
                className="w-6 h-6 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="text-[10px] text-gray-500">No Image</p>
          </div>
        </div>
      );
    }

    if (format === "DOCUMENT" && headerMediaUrl) {
      return (
        <div className="mb-2 p-2 bg-white/50 rounded-lg flex items-center gap-2 border border-gray-200/50">
          <div className="p-1.5 bg-white rounded">
            <svg
              className="w-5 h-5 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-[#111B21]">
              {templateData.headerFile?.name || "Document"}
            </p>
            <a
              href={headerMediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-blue-600 hover:underline"
            >
              View Document
            </a>
          </div>
        </div>
      );
    }

    if (format === "DOCUMENT" && !headerMediaUrl) {
      return (
        <div className="mb-2 p-2 bg-white/50 rounded-lg flex items-center gap-2 border border-gray-200/50">
          <div className="p-1.5 bg-white rounded">
            <svg
              className="w-5 h-5 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-[#111B21]">No Document</p>
          </div>
        </div>
      );
    }

    if (format === "VIDEO" && headerMediaUrl) {
      return (
        <div className="mb-2 -mx-1 -mt-1 rounded-t-lg overflow-hidden bg-gray-50 flex items-center justify-center">
          <video
            src={headerMediaUrl}
            className="max-w-full max-h-48 w-auto h-auto object-contain"
            controls
          />
        </div>
      );
    }

    if (format === "VIDEO" && !headerMediaUrl) {
      return (
        <div className="mb-2 -mx-1 -mt-1 bg-black h-32 rounded-t-lg flex items-center justify-center relative">
          <div className="text-center">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-1">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
              </svg>
            </div>
            <p className="text-[10px] text-white/75">No Video</p>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderBody = () => {
    let bodyText =
      templateData.body || "Your message will appear here. Add text above.";

    // Replace variables with values based on toggle state
    templateData.variables?.forEach((variable) => {
      const regex = new RegExp(`\\{\\{${variable.name}\\}\\}`, "g");
      // Replace with value from defaultValues, sample, or placeholder
      bodyText = bodyText.replace(regex, getVariableValue(variable.name, "BODY"));
    });

    // Convert markdown-style formatting (only apply to text that was already formatted)
    bodyText = bodyText
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<del>$1</del>");

    return (
      <div
        className="text-[#111B21] whitespace-pre-wrap break-words text-[13px] leading-[18px]"
        dangerouslySetInnerHTML={{ __html: bodyText }}
      />
    );
  };

  const renderButtons = () => {
    if (
      !templateData.buttons ||
      templateData.buttons.length === 0
    )
      return null;

    return (
      <div className="mt-2 -mx-1 -mb-1 border-t border-gray-200/50">
        {templateData.buttons.map((button, index) => {
          let icon = "↩️";
          if (button.type === "URL") icon = "🔗";
          if (button.type === "PHONE_NUMBER") icon = "📞";

          return (
            <button
              key={button.id || index}
              className={`w-full py-2 text-center text-[12px] font-medium text-[#027EB5] hover:bg-black/5 transition-colors ${
                index < templateData.buttons.length - 1
                  ? "border-b border-gray-200/50"
                  : ""
              }`}
            >
              <span className="mr-1">{icon}</span>
              <span className="truncate">
                {button.text || `Button ${index + 1}`}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm lg:overflow-visible" style={{ backgroundColor: "#e9eeea" }}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900">
            WhatsApp Preview
          </h3>
          {/* Toggle Button */}
          <button
            onClick={() => setShowVariables(!showVariables)}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-xl transition-all shadow-sm border ${
              showVariables
                ? "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200"
                : "text-gray-900 border-gray-300 hover:shadow-md"
            }`}
            style={{ backgroundColor: showVariables ? undefined : "#eefa94" }}
            title={showVariables ? "Show sample values" : "Show variable placeholders"}
          >
            {showVariables ? (
              <>
                <Eye size={14} />
                <span>Show Values</span>
              </>
            ) : (
              <>
                <Code size={14} />
                <span>Show Variables</span>
              </>
            )}
          </button>
        </div>
        <div
          className="bg-[#111B21] rounded-[1.5rem] p-1.5 shadow-xl mx-auto"
          style={{ maxWidth: "280px", height: "500px" }}
        >
          <div className="bg-black h-5 rounded-t-[1.2rem] mb-0.5 relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-[#111B21] w-20 h-3.5 rounded-b-xl"></div>
          </div>
          <div
            className="bg-white rounded-[1.2rem] overflow-hidden flex flex-col"
            style={{ height: "calc(100% - 24px)" }}
          >
            <div className="bg-[#008069] px-3 py-2.5 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <button className="text-white flex-shrink-0">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-4 h-4"
                    fill="currentColor"
                  >
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                  </svg>
                </button>
                <div className="w-7 h-7 rounded-full bg-[#00A884] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {selectedWaba?.phoneName 
                    ? selectedWaba.phoneName.charAt(0).toUpperCase() 
                    : selectedWaba?.phoneNumber 
                    ? selectedWaba.phoneNumber.slice(-2)
                    : "BC"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-xs truncate">
                    {selectedWaba?.phoneName || selectedWaba?.phoneNumber || "Business"}
                  </p>
                  <p className="text-[#D1F4CC] text-[10px]">
                    {selectedWaba?.phoneNumber || "Tap for info"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-white flex-shrink-0">
                <Video size={16} />
                <Phone size={16} />
                <MoreVertical size={16} />
              </div>
            </div>
            <div
              className="flex-1 p-3 relative overflow-y-auto"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23d1d7db' fill-opacity='0.08'%3E%3Cpath opacity='.5' d='M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundColor: "#EFEAE2",
              }}
            >
              <div className="flex justify-start">
                <div className="max-w-[85%]">
                  <div className="bg-white rounded-lg shadow-sm p-2 relative">
                    <div className="absolute -left-1.5 top-0 w-2 h-3 overflow-hidden">
                      <div className="absolute bg-white w-2 h-2 transform rotate-45 -right-0.5 top-0.5 shadow-sm"></div>
                    </div>
                    <div className="px-1 py-0.5">
                      {renderHeader()}
                      {renderBody()}
                      {templateData.footer && (
                        <div className="mt-1.5 pt-1.5 text-[10px] text-gray-500 border-t border-gray-100">
                          {templateData.footer}
                        </div>
                      )}
                      {renderButtons()}
                      <div className="flex items-center justify-end gap-0.5 mt-1">
                        <span className="text-[9px] text-gray-500">
                          {new Date().toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-[#F0F2F5] px-2.5 py-2 flex items-center gap-1.5 flex-shrink-0">
              <button className="p-1 text-gray-600 hover:text-gray-800 flex-shrink-0">
                <Smile size={18} />
              </button>
              <div className="flex-1 bg-white rounded-full px-3 py-2 flex items-center">
                <span className="text-xs text-gray-500 font-medium">Type a message</span>
              </div>
              <button className="p-1 text-gray-600 hover:text-gray-800 flex-shrink-0">
                <Paperclip size={18} />
              </button>
              <button className="p-1 text-gray-600 hover:text-gray-800 flex-shrink-0">
                <Mic size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppNewPreview;
